import React from "react";
import { Link } from "react-router-dom";
import "./CallToAction.css";

type IconName =
  | "message"
  | "target"
  | "team"
  | "shield"
  | "speed"
  | "arrow";

interface BenefitItem {
  title: string;
  description: string;
  icon: IconName;
}

const benefits: BenefitItem[] = [
  {
    title: "Domain Expertise",
    description: "Deep semiconductor engineering knowledge",
    icon: "target",
  },
  {
    title: "Flexible Engagement",
    description: "Consulting or dedicated engineering teams",
    icon: "team",
  },
  {
    title: "Reliable Delivery",
    description: "Disciplined execution with quality you can trust",
    icon: "shield",
  },
  {
    title: "End-to-End Support",
    description: "From concept to silicon realization",
    icon: "speed",
  },
];

const Icon: React.FC<{
  name: IconName;
  size?: number;
}> = ({ name, size = 34 }) => {
  const commonProps = {
    width: size,
    height: size,
    viewBox: "0 0 48 48",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  const icons: Record<IconName, React.ReactNode> = {
    message: (
      <>
        <path d="M8 9h32v24H22l-9 7v-7H8V9Z" />
        <circle cx="17" cy="21" r="1.5" />
        <circle cx="24" cy="21" r="1.5" />
        <circle cx="31" cy="21" r="1.5" />
      </>
    ),

    target: (
      <>
        <circle cx="22" cy="24" r="16" />
        <circle cx="22" cy="24" r="10" />
        <circle cx="22" cy="24" r="4" />
        <path d="M26 20 42 4" />
        <path d="M34 4h8v8" />
      </>
    ),

    team: (
      <>
        <circle cx="17" cy="17" r="6" />
        <circle cx="32" cy="17" r="6" />
        <path d="M5 40c1-9 6-14 12-14s11 5 12 14" />
        <path d="M24 31c2-3 5-5 9-5 6 0 10 5 11 14" />
      </>
    ),

    shield: (
      <>
        <path d="M24 5 39 11v11c0 10-6 17-15 21C15 39 9 32 9 22V11l15-6Z" />
        <path d="m17 24 5 5 10-12" />
      </>
    ),

    speed: (
      <>
        <path d="M7 34a18 18 0 1 1 34 0" />
        <path d="M24 24 35 14" />
        <circle cx="24" cy="24" r="3" />
        <path d="M11 29h4M33 29h4M24 8v4M12 15l3 3M36 15l-3 3" />
      </>
    ),

    arrow: (
      <>
        <path d="M8 24h30" />
        <path d="m29 15 9 9-9 9" />
      </>
    ),
  };

  return <svg {...commonProps}>{icons[name]}</svg>;
};

const CallToAction: React.FC = () => {
  return (
    <section className="dc-final-cta">
  <div className="container-fluid">
    <div className="dc-final-cta__wrap mlr-20 sec-border">
      <div className="dc-final-cta__label">
        <span />
        Let&apos;s Build
      </div>

      <div className="dc-final-cta__main">
        <div className="dc-final-cta__heading">
          <h2>
            Let&apos;s Build
            <span>Reliable Silicon Together.</span>
          </h2>
        </div>

        <div className="dc-final-cta__content">
          <p>
            Whether you&apos;re developing a next-generation semiconductor
            product, expanding engineering capacity, seeking specialized
            semiconductor engineering expertise, or exploring flexible
            engagement models, Digicomm Semiconductor is ready to support your
            engineering journey with disciplined execution and dependable
            delivery.
          </p>

          <a className="dc-final-cta__button" href="/contact">
            <span>Talk to Our Engineering Team</span>
            <span aria-hidden="true">↗</span>
          </a>
        </div>
      </div>

      <div className="dc-final-cta__footer">
        <span>Domain Expertise</span>
        <span>Flexible Engagement</span>
        <span>Reliable Delivery</span>
        <span>End-to-End Support</span>
      </div>
    </div>
  </div>
</section>
  );
};

export default CallToAction;